interface IImg {
  small: {
    src: string
    width: string
  }
  large: {
    src: string
    width: string
  }
  alt?: string
}

export interface IJourneyDetails {
  timing: string[]
  deliverables: string[]
  customer: string[]
}

export interface IJourneyPage {
  title: string
  time: string
  thumbnail: string
  descriptionText: string[]
  slug: string
  subtitle: string
  details: IJourneyDetails
  meta: {
    title: string
    description: string
  }
  "ycbm-params": string
  bstImg: {
    square: IImg
    portrait: IImg
    landscape: IImg
    alt: string
  }
  bstVideo: {
    id: string
    title: string
  }
}

export interface IJourneyCategory {
  title: string
  heading: string
  bstImg: IImg
  pages: {
    [key: string]: IJourneyPage
  }
  time?: string
}

interface IJourneyPages {
  refinance: IJourneyCategory
  purchase: IJourneyCategory
}

import purchasingAHomeSquare540w from "images/start/purchasing-a-home-square-540w.jpg"
import purchasingAHomeSquare1080w from "images/start/purchasing-a-home-square-1080w.jpg"
import thinkingOfBuyingSquare270w from "images/start/thinking-of-buying-square-270w.jpg"
import thinkingOfBuyingSquare540w from "images/start/thinking-of-buying-square-540w.jpg"

export const journeys: IJourneyPages = {
  purchase: {
    title: "I'm purchasing a home",
    heading: "Okay, where along the journey are you?",
    bstImg: {
      small: {
        src: purchasingAHomeSquare540w,
        width: "540w",
      },
      large: {
        src: purchasingAHomeSquare1080w,
        width: "1080w",
      },
      alt: "I'm purchasing a home",
    },
    pages: {
      "thinking-of-buying": {
        title: "I’m thinking about buying",
        subtitle: "Basic pre-approval",
        time: "3 minutes",
        thumbnail:
          "https://media.better.com/components/start/thinking-of-buying-landscape.jpg",
        descriptionText: [
          "Considering buying a home? Tell us a bit about your income, assets, and the area you’re looking in. We’ll instantly find the best possible loans available to you, and give you a good estimate of how much you can borrow. Getting pre-approved will not affect your credit score in any way. ",
        ],
        slug: "purchase-considering-buying",
        details: {
          timing: ["3 minutes"],
          deliverables: [
            "An estimate for what you can borrow",
            "Your credit score",
            "A basic pre-approval letter",
          ],
          customer: [
            "You just want a quick, ballpark estimate for your home purchase budget",
          ],
        },
        meta: {
          title: "Thinking about Buying",
          description:
            "Considering buying a home? Tell us a bit about yourself and the area you’re looking in. We’ll instantly find the best possible loans available to you, and give you a good estimate of how much you can borrow. Getting pre-approved will not affect your credit score in any way. ",
        },
        "ycbm-params":
          "TRACKINGLOCATION=start-pur-preapp&TRACKINGBRANDING=purchase&loanType=purchase",
        bstImg: {
          square: {
            small: {
              src: thinkingOfBuyingSquare540w,
              width: "270w",
            },
            large: {
              src: thinkingOfBuyingSquare540w,
              width: "540w",
            },
          },
          landscape: {
            small: {
              src:
                "https://media.better.com/components/start/thinking-of-buying-header-landscape-960w.jpg",
              width: "960w",
            },
            large: {
              src:
                "https://media.better.com/components/start/thinking-of-buying-header-landscape-1920w.jpg",
              width: "1920w",
            },
          },
          portrait: {
            small: {
              src:
                "https://media.better.com/components/start/thinking-of-buying-header-portrait-540w.jpg",
              width: "540w",
            },
            large: {
              src:
                "https://media.better.com/components/start/thinking-of-buying-header-portrait-1080w.jpg",
              width: "1080w",
            },
          },
          alt: "Thinking about Buying",
        },
        bstVideo: {
          id: "what-is-better-2",
          title: "Why we're Better",
        },
      },
      "shopping-for-a-home": {
        title: "I’m shopping for a home",
        subtitle: "Verified pre-approval",
        time: "20 minutes",
        thumbnail:
          "https://media.better.com/components/start/get-verified-landscape.jpg",
        descriptionText: [
          "Ready to find your dream home? Verify your income, assets, and debts, so you can negotiate your offer with more confidence.",
        ],
        slug: "purchase-shopping",
        details: {
          timing: [
            "20 minutes to upload documents, followed by a Better review",
          ],
          deliverables: [
            "Verified pre-approval letter",
            "Confidence in what you can afford",
            "Accelerated closing",
          ],
          customer: [
            "You want certainty about how much you can afford",
            "You have a complicated financial situation",
            "You are shopping in a competitive market and want to compete against cash buyers",
          ],
        },
        meta: {
          title: "Shopping for a Home",
          description:
            "In a hot market? A verified pre-approval puts you just a few steps from finishing your loan so you can shop for your dream home with confidence.",
        },
        "ycbm-params":
          "TRACKINGLOCATION=start-pur-vpal&TRACKINGBRANDING=purchase&loanType=purchase",
        bstImg: {
          square: {
            small: {
              src:
                "https://media.better.com/components/start/get-verified-square-270w.jpg",
              width: "270w",
            },
            large: {
              src:
                "https://media.better.com/components/start/get-verified-square-540w.jpg",
              width: "540w",
            },
          },
          landscape: {
            small: {
              src:
                "https://media.better.com/components/start/get-verified-header-landscape-960w.jpg",
              width: "960w",
            },
            large: {
              src:
                "https://media.better.com/components/start/get-verified-header-landscape-1920w.jpg",
              width: "1920w",
            },
          },
          portrait: {
            small: {
              src:
                "https://media.better.com/components/start/get-verified-header-portrait-540w.jpg",
              width: "540w",
            },
            large: {
              src:
                "https://media.better.com/components/start/get-verified-header-portrait-1080w.jpg",
              width: "1080w",
            },
          },
          alt: "Shopping for a Home",
        },
        bstVideo: {
          id: "shopping-for-a-home",
          title: "Why should I get verified?",
        },
      },
      "found-a-property": {
        title: "I found a property I love",
        subtitle: "",
        time: "30 minutes",
        thumbnail:
          "https://media.better.com/components/start/property-i-love-landscape.jpg",
        descriptionText: [
          "Signed a purchase contract? We won't let this property get away. We will lock your rate within 30 minutes, provide you with a dedicated Mortgage Expert, and do everything to close your loan on time.",
        ],
        slug: "purchase-found-property",
        details: {
          timing: ["30 minutes to lock", "As few as 21 days to close"],
          deliverables: [
            "Rate lock, any time",
            "Dedicated Mortgage Expert",
            "Accelerated, 21-day closing",
          ],
          customer: ["People with signed offers"],
        },
        meta: {
          title: "I Found a Property",
          description:
            "Signed a purchase contract? We won't let this property get away. We will lock your rate within 30 minutes, provide you with a dedicated Mortgage Expert, and do everything to close your loan on time.",
        },
        "ycbm-params":
          "TRACKINGLOCATION=start-pur-contract&TRACKINGBRANDING=purchase&loanType=purchase",
        bstImg: {
          square: {
            small: {
              src:
                "https://media.better.com/components/start/property-i-love-square-270w.jpg",
              width: "270w",
            },
            large: {
              src:
                "https://media.better.com/components/start/property-i-love-square-540w.jpg",
              width: "540w",
            },
          },
          landscape: {
            small: {
              src:
                "https://media.better.com/components/start/property-i-love-header-landscape-960w.jpg",
              width: "960w",
            },
            large: {
              src:
                "https://media.better.com/components/start/property-i-love-header-landscape-1920w.jpg",
              width: "1920w",
            },
          },
          portrait: {
            small: {
              src:
                "https://media.better.com/components/start/property-i-love-header-portrait-540w.jpg",
              width: "540w",
            },
            large: {
              src:
                "https://media.better.com/components/start/property-i-love-header-portrait-1080w.jpg",
              width: "1080w",
            },
          },
          alt: "I Found a Property",
        },
        bstVideo: {
          id: "rate-lock",
          title: "Why do I need to lock a rate?",
        },
      },
    },
  },
  refinance: {
    title: "I'm refinancing",
    heading: "What are your goals?",
    bstImg: {
      small: {
        src:
          "https://media.better.com/components/start/refinance-square-540w.jpg",
        width: "540w",
      },
      large: {
        src:
          "https://media.better.com/components/start/refinance-square-1080w.jpg",
        width: "1080w",
      },
      alt: "I'm refinancing",
    },
    pages: {
      "better-rate": {
        title: "I want better rates / terms",
        subtitle: "",
        time: "3 minutes",
        thumbnail:
          "https://media.better.com/components/start/want-better-rates-landscape.jpg",
        descriptionText: [
          "We'll help you get a better rate, save on monthly payments, or change the type or length of your loan.",
        ],
        slug: "refinance-better-rate",
        "ycbm-params":
          "TRACKINGLOCATION=start-refi-rate-term&TRACKINGBRANDING=refinance&loanType=refinance",
        details: {
          timing: [
            "3 minutes to see if you qualify",
            "30 minutes to lock your Better rate",
            "30 days to close",
          ],
          deliverables: [
            "Rate lock, any time",
            "An instant Loan Estimate",
            "The Better Price Guarantee",
          ],
          customer: [
            "You want to save money on your existing payments with a lower rate",
            "You want to pay off your mortgage in less time",
            "You want to change your loan type, (e.g. from an adjustable to a fixed-rate)",
          ],
        },
        meta: {
          title: "Get a Better Rate",
          description:
            "Get a better interest rate, save on monthly payments, or change the type or length of your loan with a rate and term refinance.",
        },
        bstImg: {
          square: {
            small: {
              src:
                "https://media.better.com/components/start/want-better-rates-square-270w.jpg",
              width: "270w",
            },
            large: {
              src:
                "https://media.better.com/components/start/want-better-rates-square-540w.jpg",
              width: "540w",
            },
          },
          landscape: {
            small: {
              src:
                "https://media.better.com/components/start/want-better-rates-header-landscape-960w.jpg",
              width: "960w",
            },
            large: {
              src:
                "https://media.better.com/components/start/want-better-rates-header-landscape-1920w.jpg",
              width: "1920w",
            },
          },
          portrait: {
            small: {
              src:
                "https://media.better.com/components/start/want-better-rates-header-portrait-540w.jpg",
              width: "540w",
            },
            large: {
              src:
                "https://media.better.com/components/start/want-better-rates-header-portrait-1080w.jpg",
              width: "1080w",
            },
          },
          alt: "Get a Better Rate",
        },
        bstVideo: {
          id: "reasons-to-refinance",
          title: "When should I refinance?",
        },
      },
      "getting-cash-out": {
        title: "I'm taking cash out",
        subtitle: "",
        time: "3 minutes",
        thumbnail:
          "https://media.better.com/components/start/taking-cash-out-landscape.jpg",
        descriptionText: [
          "Use the equity you've built in your home to borrow money at a low cost.",
        ],
        slug: "refinance-getting-cash-out",
        "ycbm-params":
          "TRACKINGLOCATION=start-refi-cashout&TRACKINGBRANDING=refinance&loanType=refinance",
        details: {
          timing: [
            "3 minutes to see how much cash you can take out",
            "30 minutes to lock a rate",
            "30 days to get your cash",
          ],
          deliverables: [
            "Rate lock, any time",
            "An instant Loan Estimate",
            "The Better Price Guarantee",
          ],
          customer: [
            "You want to make home renovations",
            "You need extra money for expenses such as tuition or medical costs",
          ],
        },
        meta: {
          title: "Take Cash Out",
          description:
            "Use the equity in your home to borrow money at a low cost with a cash out refinance.",
        },
        bstImg: {
          square: {
            small: {
              src:
                "https://media.better.com/components/start/taking-cash-out-square-270w.jpg",
              width: "270w",
            },
            large: {
              src:
                "https://media.better.com/components/start/taking-cash-out-square-540w.jpg",
              width: "540w",
            },
          },
          landscape: {
            small: {
              src:
                "https://media.better.com/components/start/taking-cash-out-header-landscape-960w.jpg",
              width: "960w",
            },
            large: {
              src:
                "https://media.better.com/components/start/taking-cash-out-header-landscape-1920w.jpg",
              width: "1920w",
            },
          },
          portrait: {
            small: {
              src:
                "https://media.better.com/components/start/taking-cash-out-header-portrait-540w.jpg",
              width: "540w",
            },
            large: {
              src:
                "https://media.better.com/components/start/taking-cash-out-header-portrait-1080w.jpg",
              width: "1080w",
            },
          },
          alt: "Take Cash Out",
        },
        bstVideo: {
          id: "cash-out-refinance",
          title: "Why should I refinance?",
        },
      },
      "consolidating-debt": {
        title: "I'm consolidating debt",
        subtitle: "",
        time: "3 minutes",
        thumbnail:
          "https://media.better.com/components/start/consolidating-debt-landscape.jpg",
        descriptionText: [
          "Save money long term by consolidating your high interest rate loans and debts into a single lower interest rate home loan.",
        ],
        slug: "refinance-consolidating-debt",
        "ycbm-params":
          "TRACKINGLOCATION=start-refi-consolidate-debt&TRACKINGBRANDING=refinance&loanType=refinance",
        details: {
          timing: [
            "3 minutes to determine if you qualify",
            "30 minutes to lock a rate",
            "30 days to pay off your debts",
          ],
          deliverables: [
            "Rate lock, any time",
            "An instant Loan Estimate",
            "The Better Price Guarantee",
          ],
          customer: [
            "You want to consolidate other debts into a single, more affordable payment",
            "You have student debt at a high interest rate",
            "You have credit cards you haven't been able to pay off",
          ],
        },
        meta: {
          title: "Consolidate Debt",
          description:
            "Use a refinance to consolidate your high interest rate loans and debts into a single lower interest rate home loan with a debt consolidation refinance.",
        },
        bstImg: {
          square: {
            small: {
              src:
                "https://media.better.com/components/start/consolidating-debt-square-270w.jpg",
              width: "270w",
            },
            large: {
              src:
                "https://media.better.com/components/start/consolidating-debt-square-540w.jpg",
              width: "540w",
            },
          },
          landscape: {
            small: {
              src:
                "https://media.better.com/components/start/consolidating-debt-header-landscape-960w.jpg",
              width: "960w",
            },
            large: {
              src:
                "https://media.better.com/components/start/consolidating-debt-header-landscape-1920w.jpg",
              width: "1920w",
            },
          },
          portrait: {
            small: {
              src:
                "https://media.better.com/components/start/consolidating-debt-header-portrait-540w.jpg",
              width: "540w",
            },
            large: {
              src:
                "https://media.better.com/components/start/consolidating-debt-header-portrait-1080w.jpg",
              width: "1080w",
            },
          },
          alt: "Consolidate Debt",
        },
        bstVideo: {
          id: "consolidating-debt",
          title: "Why would I use a mortgage to consolidate debt?",
        },
      },
    },
  },
}
